import { useState } from 'react'
import {
  CircleDollarSign,
  Info,
  Check,
  AlertTriangle,
  XCircle,
  CheckCircle,
  HardDrive,
} from 'lucide-react'

// UI Components
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar } from '@/components/ui/avatar'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'

// Services & Utils
import {
  useListNotifications,
  useMarkNotificationRead,
  useTotalUnreadCount,
  useMarkAllNotificationsRead,
} from '@/services/notification'
import { cn } from '@/lib/utils'

// Local components
import { DefaultNotification } from './DefaultNotification'
import { TopUpNotification } from './TopUpNotification'
import { SpendingLimitNotification } from './SpendingLimitNotification'
import { ServerFailureNotification } from './ServerFailureNotification'
import { ShutdownResourceNotification } from './ShutdownResourceNotification'
import { ErrorMessage } from '@/components/error-message'
import { LoadingMessage } from '@/components/loading-message'

// Types
import {
  NotificationTypeIconProps,
  NotificationItemProps,
  MarkAsReadButtonProps,
} from './types'
import { toast } from 'react-hot-toast'
import { UserSpendingLimitNotification } from './UserSpendingLimitNotification'

// MAIN EXPORTED COMPONENT
export const NotificationsTab = () => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useListNotifications(20)

  const markNotificationRead = useMarkNotificationRead()
  const markAllNotificationsReadMutation = useMarkAllNotificationsRead()

  const [loadingId, setLoadingId] = useState<number | null>(null)
  const [markAllLoading, setMarkAllLoading] = useState(false)

  const allNotifications = data?.allNotifications || []
  const { data: totalUnreadCount = 0 } = useTotalUnreadCount()

  if (isLoading)
    return (
      <LoadingMessage
        message="Loading notifications..."
        centered
        className="my-10"
      />
    )

  if (isError) {
    return (
      <div className="mx-auto max-w-2xl my-10">
        <ErrorMessage message="Failed to load notifications." />
      </div>
    )
  }

  if (allNotifications.length === 0) {
    return (
      <Alert className="mx-auto max-w-2xl my-10">
        <Info className="h-4 w-4" />
        <AlertTitle>No Notifications</AlertTitle>
        <AlertDescription>
          You don't have any notifications at this time.
        </AlertDescription>
      </Alert>
    )
  }

  const onMarkAsRead = (notificationId: number) => {
    setLoadingId(notificationId)
    markNotificationRead.mutate(notificationId, {
      onSuccess: () => {
        const notification = allNotifications.find(
          (n) => n.notification_id === notificationId
        )
        if (notification) notification.is_read = true
        setLoadingId(null)

        toast.success('Notification marked as read')
      },
      onError: (error) => {
        console.error(error)
        toast.error(JSON.stringify(error))
        setLoadingId(null)
      },
    })
  }

  const onMarkAllAsRead = () => {
    setMarkAllLoading(true)
    markAllNotificationsReadMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('All notifications marked as read')
        setMarkAllLoading(false)
      },
      onError: (error) => {
        console.error(error)
        toast.error(JSON.stringify(error))
        setMarkAllLoading(false)
      },
    })
  }

  return (
    <div className="max-w-3xl mt-6">
      <header
        className="mb-6 flex justify-between items-center"
        data-testid="notifications-header"
      >
        <h2 className="text-2xl font-bold" data-testid="notifications-title">
          Notifications
        </h2>
        <div className="flex items-center gap-4">
          {totalUnreadCount > 0 && (
            <Badge
              variant="destructive"
              className="text-sm px-3 py-1"
              data-testid="unread-count-badge"
            >
              {`${totalUnreadCount} unread`}
              {/* {totalUnreadCount} unread */}
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onMarkAllAsRead}
            disabled={markAllLoading || totalUnreadCount === 0}
            className="flex items-center gap-2"
            data-testid="mark-all-read-button"
          >
            {markAllLoading ? (
              <LoadingMessage size="sm" className="p-0" />
            ) : (
              <Check className="h-3 w-3" />
            )}
            <span>{markAllLoading ? 'Marking...' : 'Mark all as read'}</span>
          </Button>
        </div>
      </header>

      <div className="space-y-4" data-testid="notifications-list">
        {allNotifications.map((notification) => (
          <NotificationItem
            key={notification.notification_id}
            notification={notification}
            isLoading={loadingId === notification.notification_id}
            onMarkAsRead={onMarkAsRead}
          />
        ))}
      </div>

      {isFetchingNextPage ? (
        <div className="text-center py-4">
          <LoadingMessage message="Loading more notifications..." size="sm" />
        </div>
      ) : hasNextPage ? (
        <div className="text-center py-4">
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            Load 20 more
          </Button>
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-xs text-foreground/60">End of notifications</p>
        </div>
      )}
    </div>
  )
}

// Helper function for smart categorization
const getSmartCategory = (type: string, title?: string, detail?: string) => {
  const text = `${type} ${title || ''} ${detail || ''}`.toLowerCase()

  const errorKeywords = [
    'failed',
    'failure',
    'error',
    'critical',
    'down',
    'offline',
    'crashed',
    'terminated',
  ]
  if (errorKeywords.some((keyword) => text.includes(keyword))) {
    return 'error'
  }

  const warningKeywords = [
    'warning',
    'alert',
    'limit',
    'capacity',
    'exceeded',
    'high',
    'pending',
    'suspended',
  ]
  if (warningKeywords.some((keyword) => text.includes(keyword))) {
    return 'warning'
  }

  const successKeywords = [
    'success',
    'completed',
    'created',
    'added',
    'approved',
    'restored',
    'resolved',
  ]
  if (successKeywords.some((keyword) => text.includes(keyword))) {
    return 'success'
  }

  const storageKeywords = ['storage', 'disk', 'volume', 'space', 'quota']
  if (storageKeywords.some((keyword) => text.includes(keyword))) {
    return 'storage'
  }

  const creditKeywords = [
    'credit',
    'payment',
    'billing',
    'invoice',
    'balance',
    'topup',
    'charge',
  ]
  if (creditKeywords.some((keyword) => text.includes(keyword))) {
    return 'credit'
  }

  return 'info'
}

// Child components
const NotificationItem = ({
  notification,
  isLoading,
  onMarkAsRead,
}: NotificationItemProps) => {
  // Get accent color based on notification type
  const getAccentColor = () => {
    switch (notification.notification_type) {
      case 'spending_limit':
      case 'user_spending_limit':
        return 'border-l-4 border-destructive'
      case 'add_org_credit':
        return 'border-l-4 border-emerald-600'
      case 'server_failure':
      case 'server_capacity_exceeded':
        return 'border-l-4 border-amber-600'
      case 'ephemeral_storage_alert':
        return 'border-l-4 border-orange-600'
      default: {
        // Use smart categorization for unknown types
        const category = getSmartCategory(
          notification.notification_type,
          notification.title,
          notification.detail
        )
        switch (category) {
          case 'error':
            return 'border-l-4 border-destructive'
          case 'warning':
            return 'border-l-4 border-amber-600'
          case 'success':
          case 'credit':
            return 'border-l-4 border-emerald-600'
          case 'storage':
            return 'border-l-4 border-orange-600'
          default:
            return 'border-l-4 border-blue-600'
        }
      }
    }
  }

  return (
    <div
      className="relative notification-card"
      data-testid={`notification-item-${notification.notification_id}`}
    >
      {!notification.is_read && (
        <div
          className="absolute top-3 right-3 w-2 h-2 rounded-full bg-primary animate-pulse z-10"
          data-testid="unread-indicator"
        />
      )}
      <Card
        className={cn(
          'overflow-hidden transition-all',
          notification.is_read
            ? 'opacity-60 bg-background/50 grayscale'
            : 'bg-background shadow-md',
          getAccentColor()
        )}
        data-testid="notification-card"
      >
        {notification.notification_type === 'spending_limit' ? (
          <SpendingLimitNotification
            notification={notification}
            isLoading={isLoading}
            onMarkAsRead={onMarkAsRead}
          />
        ) : notification.notification_type === 'user_spending_limit' ? (
          <UserSpendingLimitNotification
            notification={notification}
            isLoading={isLoading}
            onMarkAsRead={onMarkAsRead}
          />
        ) : notification.notification_type === 'add_org_credit' ? (
          <TopUpNotification
            notification={notification}
            isLoading={isLoading}
            onMarkAsRead={onMarkAsRead}
          />
        ) : notification.notification_type === 'server_failure' ? (
          <ServerFailureNotification
            notification={notification}
            isLoading={isLoading}
            onMarkAsRead={onMarkAsRead}
          />
        ) : notification.notification_type === 'shutdown_user_resource' ? (
          <ShutdownResourceNotification
            notification={notification}
            isLoading={isLoading}
            onMarkAsRead={onMarkAsRead}
          />
        ) : (
          <DefaultNotification
            notification={notification}
            isLoading={isLoading}
            onMarkAsRead={onMarkAsRead}
          />
        )}
      </Card>
    </div>
  )
}

export const NotificationTypeIcon = ({
  type,
  title,
  detail,
}: NotificationTypeIconProps) => {
  const iconMap = {
    spending_limit: {
      bg: 'bg-destructive/10 dark:bg-destructive/20',
      icon: <CircleDollarSign className="h-4 w-4 text-destructive" />,
    },
    user_spending_limit: {
      bg: 'bg-destructive/10 dark:bg-destructive/20',
      icon: <CircleDollarSign className="h-4 w-4 text-destructive" />,
    },
    add_org_credit: {
      bg: 'bg-emerald-600/20 dark:bg-emerald-600/30',
      icon: <CircleDollarSign className="h-4 w-4 text-emerald-600" />,
    },
    add_user_credit: {
      bg: 'bg-emerald-600/20 dark:bg-emerald-600/30',
      icon: <CircleDollarSign className="h-4 w-4 text-emerald-600" />,
    },
    shutdown_user_resource: {
      bg: 'bg-amber-500/10 dark:bg-amber-500/20',
      icon: <AlertTriangle className="h-4 w-4 text-destructive" />,
    },
    server_failure: {
      bg: 'bg-amber-500/10 dark:bg-amber-500/20',
      icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    },
    server_capacity_exceeded: {
      bg: 'bg-amber-500/10 dark:bg-amber-500/20',
      icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    },
    ephemeral_storage_alert: {
      bg: 'bg-orange-500/10 dark:bg-orange-500/20',
      icon: <AlertTriangle className="h-4 w-4 text-orange-600" />,
    },
  }

  // Smart categorization for unknown types
  const getSmartIcon = (type: string, title?: string, detail?: string) => {
    const text = `${type} ${title || ''} ${detail || ''}`.toLowerCase()

    // Critical/Error patterns
    const errorKeywords = [
      'failed',
      'failure',
      'error',
      'critical',
      'down',
      'offline',
      'crashed',
      'terminated',
    ]
    if (errorKeywords.some((keyword) => text.includes(keyword))) {
      return {
        bg: 'bg-destructive/10 dark:bg-destructive/20',
        icon: <XCircle className="h-4 w-4 text-destructive" />,
      }
    }

    // Warning patterns
    const warningKeywords = [
      'warning',
      'alert',
      'limit',
      'capacity',
      'exceeded',
      'high',
      'pending',
      'suspended',
    ]
    if (warningKeywords.some((keyword) => text.includes(keyword))) {
      return {
        bg: 'bg-amber-500/10 dark:bg-amber-500/20',
        icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
      }
    }

    // Success patterns
    const successKeywords = [
      'success',
      'completed',
      'created',
      'added',
      'approved',
      'restored',
      'resolved',
    ]
    if (successKeywords.some((keyword) => text.includes(keyword))) {
      return {
        bg: 'bg-emerald-600/20 dark:bg-emerald-600/30',
        icon: <CheckCircle className="h-4 w-4 text-emerald-600" />,
      }
    }

    // Storage patterns
    const storageKeywords = ['storage', 'disk', 'volume', 'space', 'quota']
    if (storageKeywords.some((keyword) => text.includes(keyword))) {
      return {
        bg: 'bg-orange-500/10 dark:bg-orange-500/20',
        icon: <HardDrive className="h-4 w-4 text-orange-600" />,
      }
    }

    // Credit/Payment patterns
    const creditKeywords = [
      'credit',
      'payment',
      'billing',
      'invoice',
      'balance',
      'topup',
      'charge',
    ]
    if (creditKeywords.some((keyword) => text.includes(keyword))) {
      return {
        bg: 'bg-emerald-600/20 dark:bg-emerald-600/30',
        icon: <CircleDollarSign className="h-4 w-4 text-emerald-600" />,
      }
    }

    // Default info style
    return {
      bg: 'bg-blue-500/10 dark:bg-blue-500/20',
      icon: <Info className="h-4 w-4 text-blue-600" />,
    }
  }

  // Try to find the specific icon for this notification type
  // If not found, use smart categorization
  const { bg, icon } =
    iconMap[type as keyof typeof iconMap] || getSmartIcon(type, title, detail)

  return (
    <Avatar size="sm" className={cn('flex items-center justify-center', bg)}>
      {icon}
    </Avatar>
  )
}

export const MarkAsReadButton = ({
  isLoading,
  onMarkAsRead,
}: MarkAsReadButtonProps) => (
  <Button
    variant="ghost"
    size="sm"
    onClick={onMarkAsRead}
    disabled={isLoading}
    className="text-xs"
  >
    {isLoading ? (
      <LoadingMessage className="h-3 w-3 mr-2" size="sm" />
    ) : (
      <Check className="h-3 w-3 mr-2" />
    )}
    Mark as read
  </Button>
)

export const FormattedNotificationContent = ({
  content,
}: {
  content: string
}) => {
  // Highlight dollar amounts and percentages
  const formattedContent = content
    .replace(
      /(\$[\d,]+\.?\d*)/g,
      '<span class="font-medium text-primary">$1</span>'
    )
    .replace(
      /(\d+\.?\d*\s*%)/g,
      '<span class="font-medium text-destructive">$1</span>'
    )

  return (
    <p
      className="text-sm text-foreground/80 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: formattedContent }}
    />
  )
}
